import { InboxOutlined, UploadOutlined } from '@ant-design/icons';
import { message, Upload, UploadFile, UploadProps } from 'antd';
import Button from 'components/Button';
import { RcFile } from 'antd/es/upload';
import { useEffect, useState } from 'react';

/** *
 * Overrides the default Ant Design Upload component to allow users to select files,
 * preview them, and handle file changes without automatically uploading them.
 *
 * @param {number} [maxSize=10485760] - Maximum file size in bytes, default is 10 MB
 * @param {string} [uploadShape='button'] - Shape of the upload component. Can be 'button' or 'drag'
 * @param {string} [buttonText='Click to Upload'] - Text to display on the button
 * @param {boolean} [multiple=false] - Whether to allow multiple files to be selected
 * @param {function} [onFileChange] - Callback function triggered when files are added or removed
 *   - If 'multiple' is 'false', the callback receives a single 'Blob' object or 'undefined' if no file is selected.
 *   - If 'multiple' is 'true', the callback receives an array of 'Blob' objects representing **all selected files**.
 *     For example, if there are already 5 files and a new file is added, the callback will receive an array of 6 files.
 *   - If a file is removed, the callback will receive the updated list of files (excluding the removed file).
 *
 * Example usage can be found in the Stakeholder Form pages.
 */

type FileInputCommonProps = UploadProps & {
  uploadShape?: 'button' | 'drag';
  maxSize?: number;
  buttonText?: string;
  onDelete?: (file: UploadFile) => void;
};

type SingleFileUploadProps = FileInputCommonProps & {
  onFileChange?: (file?: Blob) => void;
  multiple?: false | undefined;
};

type MultipleFileUploadProps = FileInputCommonProps & {
  onFileChange?: (files: Blob[]) => void;
  multiple: true;
};

type FileUploadInputProps = SingleFileUploadProps | MultipleFileUploadProps;

const FileUploadInput = ({
  maxSize = 10485760,
  uploadShape = 'button',
  buttonText,
  onFileChange,
  onDelete,
  multiple = false,
  ...props
}: FileUploadInputProps) => {
  const [selectedFiles, setSelectedFiles] = useState<RcFile[]>([]);
  const [previewList, setPreviewList] = useState<UploadFile[]>([]);
  const [hasLoadedDefaultFile, setLoadedDefaultFileList] = useState(false);

  useEffect(() => {
    if (
      props?.defaultFileList?.length &&
      selectedFiles?.length === 0 &&
      !hasLoadedDefaultFile
    ) {
      setPreviewList(props.defaultFileList);
      setLoadedDefaultFileList(true);
    }
  }, [props?.defaultFileList, hasLoadedDefaultFile, selectedFiles]);

  const readFileAsDataURL = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
    });
  };

  const uploadProps: UploadProps = {
    accept: props.accept ?? '.png,.jpg,.jpeg,.pdf',
    multiple,
    maxCount: !multiple ? 1 : undefined,
    fileList: previewList,
    onRemove: (file) => {
      const newFileList = selectedFiles.filter((f) => f.uid !== file.uid);
      const newPreviewList = previewList.filter((f) => f.uid !== file.uid);
      setSelectedFiles(newFileList);
      setPreviewList(newPreviewList);
      if (multiple) {
        (onFileChange as (files?: Blob[]) => void)?.(newFileList);
      } else {
        (onFileChange as (file?: Blob) => void)?.(undefined);
      }
      onDelete?.(file);
    },
    beforeUpload: async (file, fileList) => {
      if (
        fileList.length > 1 &&
        fileList.findIndex((f) => f.uid === file.uid) !== fileList.length - 1
      ) {
        return false;
      }

      try {
        const filesToAdd: UploadFile[] = [];

        for (const file of fileList) {
          if (file.size > maxSize) {
            message.error(
              `Maximum file size is ${Math.floor(maxSize / 1048576)} MB`,
            );
            return false;
          }
          const dataURL = await readFileAsDataURL(file);
          const newFile: UploadFile = {
            uid: file.uid,
            name: file.name,
            status: 'done',
            url: dataURL,
            originFileObj: file,
          };
          filesToAdd.push(newFile);
        }

        setSelectedFiles((prev) => [...prev, ...fileList]);
        setPreviewList((prev) => [...prev, ...filesToAdd]);
        if (multiple) {
          (onFileChange as (files?: Blob[]) => void)?.([
            ...selectedFiles,
            ...fileList,
          ]);
        } else {
          (onFileChange as (file?: Blob) => void)?.(file);
        }
      } catch (error) {
        console.error('Error reading file:', error);
        message.error('Failed to read file');
      }

      return false;
    },
    ...props,
  };

  return (
    <>
      {uploadShape === 'drag' ? (
        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag file to this area to upload
          </p>
          <p className="ant-upload-hint">
            {props.accept
              ? `${props.accept.split(', ')} (Max file size: ${Math.floor(maxSize / 1048576)} MB)`
              : `PNG, JPG, JPEG, PDF (Max file size: ${Math.floor(maxSize / 1048576)} MB)`}
          </p>
        </Upload.Dragger>
      ) : (
        <Upload {...uploadProps}>
          <Button icon={<UploadOutlined />}>
            {buttonText ?? 'Click to Upload'}
          </Button>
        </Upload>
      )}
    </>
  );
};

export default FileUploadInput;
